import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { createRoot } from "react-dom/client";

const ReactConfirmAlert = ({
  title,
  message,
  buttons = [
    {
      label: "Cancel",
      onClick: () => null,
      className: null,
    },
    {
      label: "Confirm",
      onClick: () => null,
      className: null,
    },
  ],
  childrenElement = () => null,
  customUI,
  closeOnClickOutside = true,
  closeOnEscape = true,
  willUnmount = () => null,
  afterClose = () => null,
  onClickOutside = () => null,
  onKeypressEscape = () => null,
  overlayClassName,
}) => {
  const overlayRef = useRef(null);

  const close = () => {
    removeBodyClass();
    removeElementReconfirm();
    removeSVGBlurReconfirm(afterClose);
  };

  const handleClickButton = (button) => {
    if (button.onClick) button.onClick();
    close();
  };

  const handleClickOverlay = (e) => {
    const isClickOutside = e.target === overlayRef.current;
    if (closeOnClickOutside && isClickOutside) {
      onClickOutside();
      close();
    }
  };

  const keyboardClose = (event) => {
    const isKeyCodeEscape = event.keyCode === 27;
    if (closeOnEscape && isKeyCodeEscape) {
      onKeypressEscape(event);
      close();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", keyboardClose, false);
    return () => {
      document.removeEventListener("keydown", keyboardClose, false);
      willUnmount();
    };
  }, []);

  const renderCustomUI = () => {
    const dataCustomUI = {
      title,
      message,
      buttons,
      onClose: close,
    };
    return customUI(dataCustomUI);
  };

  return (
    <div
      className={`react-confirm-alert-overlay ${overlayClassName}`}
      ref={overlayRef}
      onClick={handleClickOverlay}
    >
      <div className="react-confirm-alert">
        {customUI ? (
          renderCustomUI()
        ) : (
          <div className="react-confirm-alert-body">
            {title && <h1>{title}</h1>}
            {message}
            {childrenElement()}
            <div className="react-confirm-alert-button-group">
              {buttons.map((button, i) => (
                <button
                  key={i}
                  onClick={() => handleClickButton(button)}
                  className={button.className}
                >
                  {button.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ReactConfirmAlert.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  buttons: PropTypes.array.isRequired,
  childrenElement: PropTypes.func,
  customUI: PropTypes.func,
  closeOnClickOutside: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
  willUnmount: PropTypes.func,
  afterClose: PropTypes.func,
  onClickOutside: PropTypes.func,
  onKeypressEscape: PropTypes.func,
  overlayClassName: PropTypes.string,
};

export default ReactConfirmAlert;

function createSVGBlurReconfirm() {
  const svg = document.getElementById("react-confirm-alert-firm-svg");
  if (svg) return;
  const svgNS = "http://www.w3.org/2000/svg";
  const feGaussianBlur = document.createElementNS(svgNS, "feGaussianBlur");
  feGaussianBlur.setAttribute("stdDeviation", "0.3");

  const filter = document.createElementNS(svgNS, "filter");
  filter.setAttribute("id", "gaussian-blur");
  filter.appendChild(feGaussianBlur);

  const svgElem = document.createElementNS(svgNS, "svg");
  svgElem.setAttribute("id", "react-confirm-alert-firm-svg");
  svgElem.setAttribute("class", "react-confirm-alert-svg");
  svgElem.appendChild(filter);

  document.body.appendChild(svgElem);
}

function removeSVGBlurReconfirm(afterClose) {
  const svg = document.getElementById("react-confirm-alert-firm-svg");
  if (svg) {
    svg.parentNode.removeChild(svg);
  }
  document.body.children[0].classList.remove("react-confirm-alert-blur");
  afterClose();
}

function createElementReconfirm(properties) {
  let divTarget = document.getElementById("react-confirm-alert");
  if (divTarget) {
    const root = createRoot(divTarget);
    root.render(<ReactConfirmAlert {...properties} />);
  } else {
    document.body.children[0].classList.add("react-confirm-alert-blur");
    divTarget = document.createElement("div");
    divTarget.id = "react-confirm-alert";
    document.body.appendChild(divTarget);
    const root = createRoot(divTarget);
    root.render(<ReactConfirmAlert {...properties} />);
  }
}

function removeElementReconfirm() {
  const target = document.getElementById("react-confirm-alert");
  if (target) {
    const root = createRoot(target);
    root.unmount();
    target.parentNode.removeChild(target);
  }
}

function addBodyClass() {
  document.body.classList.add("react-confirm-alert-body-element");
}

function removeBodyClass() {
  document.body.classList.remove("react-confirm-alert-body-element");
}

export function confirmAlert(properties) {
  addBodyClass();
  createSVGBlurReconfirm();
  createElementReconfirm(properties);
}
